import { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Float,
  MeshDistortMaterial,
  Icosahedron,
  Environment,
  OrbitControls,
  Sparkles,
  Ring,
} from "@react-three/drei";
import * as THREE from "three";

function DistortedCore() {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.y = state.clock.getElapsedTime() * 0.15;
    ref.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.12;
  });
  return (
    <group ref={ref}>
      {/* inner glowing core */}
      <mesh>
        <sphereGeometry args={[1.35, 96, 96]} />
        <MeshDistortMaterial
          color="#0F172A"
          metalness={0.85}
          roughness={0.15}
          distort={0.42}
          speed={1.8}
        />
      </mesh>
      {/* orange wireframe icosahedron shell */}
      <Icosahedron args={[2.0, 2]}>
        <meshBasicMaterial color="#F97316" wireframe transparent opacity={0.7} />
      </Icosahedron>
      {/* emerald outer atmospheric shell */}
      <mesh>
        <sphereGeometry args={[2.45, 48, 48]} />
        <meshBasicMaterial color="#10B981" wireframe transparent opacity={0.15} />
      </mesh>
    </group>
  );
}

function OrbitRings() {
  const g = useRef();
  useFrame((state) => {
    if (!g.current) return;
    g.current.rotation.z = state.clock.getElapsedTime() * 0.05;
  });
  return (
    <group ref={g}>
      <Ring args={[2.9, 2.93, 128]} rotation={[Math.PI / 2, 0, 0]}>
        <meshBasicMaterial color="#F97316" transparent opacity={0.5} side={THREE.DoubleSide} />
      </Ring>
      <Ring args={[3.3, 3.32, 128]} rotation={[Math.PI / 2, 0.4, 0.4]}>
        <meshBasicMaterial color="#10B981" transparent opacity={0.35} side={THREE.DoubleSide} />
      </Ring>
    </group>
  );
}

export default function Hero3D() {
  return (
    <div className="absolute inset-0" data-testid="hero-3d-canvas">
      <Canvas
        camera={{ position: [0, 0.4, 6.2], fov: 45 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
      >
        <color attach="background" args={["#0B0F1A"]} />
        <ambientLight intensity={0.35} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} color="#F97316" />
        <directionalLight position={[-5, -3, 4]} intensity={0.6} color="#10B981" />
        <Suspense fallback={null}>
          <Float speed={1.4} rotationIntensity={0.6} floatIntensity={0.6}>
            <DistortedCore />
          </Float>
          <OrbitRings />
          <Sparkles count={80} scale={[10, 6, 6]} size={2.2} speed={0.4} color="#F97316" />
          <Sparkles count={40} scale={[9, 5, 5]} size={1.5} speed={0.3} color="#10B981" />
          <Environment preset="city" />
        </Suspense>
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          autoRotate
          autoRotateSpeed={0.35}
          minPolarAngle={Math.PI / 2.6}
          maxPolarAngle={Math.PI / 1.7}
        />
      </Canvas>
    </div>
  );
}
